import { ChevronDown } from "lucide-react"

import type { TokenStore } from "@/domains/tokens"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { cn } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import type { FeeTier } from "../../constants"
import type { PoolOption } from "../../stores/create-pool-store"
import { FeeTierSelectorView } from "./fee-tier-selector-view"
import { PriceInputView } from "./price-input-view"

export interface CreatePoolCardViewProps {
  // Token selection
  token0: TokenStore | undefined
  token1: TokenStore | undefined
  onToken0Select: () => void
  onToken1Select: () => void

  // Fee tier selection
  feeTiers: Array<FeeTier>
  selectedFeeTier: number | undefined
  poolOptions: Array<PoolOption>
  onFeeTierSelect: (feeTier: number) => void

  // Price input
  initialPrice: string
  marketPrice: number | undefined
  onPriceChange: (price: string) => void
  onUseMarketPrice: () => void
  useMarketPrice: boolean
  isLoadingMarketPrice: boolean

  // Pool creation
  canCreatePool: boolean
  isCreating: boolean
  creationError: string | undefined
  onCreatePool: () => void
}

/**
 * Create Pool Card View
 * Pure stateless component - matches swap UI visual style
 * Uses light levels system (P300, P400, etc.)
 * Reuses TokenInputCardView styling for token selection
 */
export function CreatePoolCardView({
  token0,
  token1,
  onToken0Select,
  onToken1Select,
  feeTiers,
  selectedFeeTier,
  poolOptions,
  onFeeTierSelect,
  initialPrice,
  marketPrice,
  onPriceChange,
  onUseMarketPrice,
  useMarketPrice,
  isLoadingMarketPrice,
  canCreatePool,
  isCreating,
  creationError,
  onCreatePool,
}: CreatePoolCardViewProps) {
  const title = "Create Pool"
  const subTitle = "Create a concentrated liquidity pool with a specific fee tier"

  return (
    <div className="group/container relative w-full max-w-2xl overflow-hidden rounded-3xl border border-p400-light bg-p300 shadow-2xl">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-p100/5 to-transparent opacity-0 transition-opacity group-hover/container:opacity-100"></div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between p-6 pb-2">
        <div>
          <BodyXl As="h2" className="font-black tracking-tight text-t100">
            {title}
          </BodyXl>
          <BodySM As="p" className="mt-1 tracking-tight text-t200">
            {subTitle}
          </BodySM>
        </div>
      </div>

      <div className="relative z-10 flex min-h-[450px] flex-col p-4">
        <div className="relative space-y-4">
          {/* Token 0 Selection */}
          <div className="relative">
            <div
              className={cn(
                "group relative overflow-hidden rounded-2xl border border-p400-light bg-p400 p-4 transition-all focus-within:border-p100 hover:border-p300-light",
              )}
            >
              {/* Header Row: Label */}
              {/* <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-bold tracking-wide text-t100">Token 0</span>
              </div> */}

              {/* Main Row: Token Selector */}
              <div className="flex items-start justify-between gap-4">
                <button
                  onClick={onToken0Select}
                  className="group/btn flex shrink-0 items-center gap-1.5 rounded-full border border-p300-light bg-p300 py-2 pl-3 pr-3 shadow-sm transition-all hover:bg-p300-light"
                >
                  {token0 ? (
                    <div className="from-purple-500 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br to-blue-500">
                      <DisplayTokens tokens={token0 ? [token0] : []} tokenSize={24} />
                    </div>
                  ) : null}
                  <BodyLg className="font-bold tracking-tight text-t100">
                    {token0 ? token0.symbol : "Select token 0"}
                  </BodyLg>
                  <ChevronDown className="h-5 w-5 text-t200 transition-colors group-hover/btn:text-t100" />
                </button>
              </div>
            </div>
          </div>

          {/* Token 1 Selection */}
          <div className="relative">
            <div
              className={cn(
                "group relative overflow-hidden rounded-2xl border border-p400-light bg-p400 p-4 transition-all focus-within:border-p100 hover:border-p300-light",
              )}
            >
              {/* Header Row: Label */}
              {/* <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-bold tracking-wide text-t100">Token 1</span>
              </div> */}

              {/* Main Row: Token Selector */}
              <div className="flex items-start justify-between gap-4">
                <button
                  onClick={onToken1Select}
                  className="group/btn flex shrink-0 items-center gap-1.5 rounded-full border border-p300-light bg-p300 py-2 pl-3 pr-3 shadow-sm transition-all hover:bg-p300-light"
                >
                  {token1 ? (
                    <div className="from-purple-500 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br to-blue-500">
                      <DisplayTokens tokens={token1 ? [token1] : []} tokenSize={24} />
                    </div>
                  ) : null}
                  <BodyLg className="font-bold tracking-tight text-t100">
                    {token1 ? token1.symbol : "Select token 1"}
                  </BodyLg>
                  <ChevronDown className="h-5 w-5 text-t200 transition-colors group-hover/btn:text-t100" />
                </button>
              </div>
            </div>
          </div>

          {/* Fee Tier Selection - Only show when both tokens selected */}
          {token0 && token1 && (
            <div>
              <FeeTierSelectorView
                feeTiers={feeTiers}
                selectedFeeTier={selectedFeeTier}
                poolOptions={poolOptions}
                onFeeTierSelect={onFeeTierSelect}
              />
            </div>
          )}

          {/* Price Input - Only show when both tokens selected */}
          {token0 && token1 && (
            <div>
              <PriceInputView
                price={initialPrice}
                marketPrice={marketPrice}
                onPriceChange={onPriceChange}
                onUseMarketPrice={onUseMarketPrice}
                useMarketPrice={useMarketPrice}
                isLoadingMarketPrice={isLoadingMarketPrice}
              />
            </div>
          )}

          {/* Create Pool Button */}
          <div className="mt-6">
            <button
              onClick={onCreatePool}
              disabled={!canCreatePool || isCreating}
              className={cn(
                "w-full rounded-xl border-2 px-6 py-4 text-lg font-bold transition-all",
                canCreatePool && !isCreating
                  ? "border-p100 bg-p100 text-t100 hover:bg-p100/90 active:scale-95"
                  : "cursor-not-allowed border-p400-light bg-p400 text-t300",
              )}
            >
              {isCreating ? "Creating Pool..." : "Create Pool"}
            </button>
          </div>

          {/* Error Display */}
          {creationError && (
            <div className="border-warning bg-warning/10 text-warning mt-4 rounded-lg border p-3 text-sm">
              {creationError}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
